import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Calendar, Search } from 'lucide-react'
import { newsApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'
import Card from '../../components/ui/Card'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import MediaImage from '../../components/ui/MediaImage'
import { fadeInUp, staggerContainer } from '../../utils/animations'
import { formatDate } from '../../utils/date'

export default function NewsList() {
  const { t } = useTranslation()
  const { localized } = useLanguage()
  const [News, setNews] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [year, setYear] = useState('All')
  const [category, setCategory] = useState('All')

  useEffect(() => {
    setLoading(true)
    newsApi
      .getAll({ search: search || undefined })
      .then(({ data }) => setNews(data.data || data || []))
      .catch(() => setNews([]))
      .finally(() => setLoading(false))
  }, [search])

  const years = useMemo(() => {
    const set = new Set(
      News
        .map((item) => item.published_at && new Date(item.published_at).getFullYear())
        .filter(Boolean),
    )
    return ['All', ...Array.from(set).sort((a, b) => b - a)]
  }, [News])

  const categories = useMemo(() => {
    const set = new Set(News.map((item) => item.category).filter(Boolean))
    return ['All', ...Array.from(set)]
  }, [News])

  const filtered = useMemo(() => {
    return News.filter((item) => {
      if (year !== 'All' && item.published_at) {
        if (new Date(item.published_at).getFullYear() !== Number(year)) return false
      }
      if (category !== 'All' && item.category !== category) return false
      return true
    })
  }, [News, year, category])

  return (
    <div className="header-offset">
      <div className="gradient-brand py-12 text-white sm:py-16">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl font-bold sm:text-4xl"
          >
            {t('news.title')}
          </motion.h1>
          <p className="mt-4 text-white/80">{t('news.subtitle')}</p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <div className="mb-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-4">
          <div className="relative min-w-0 flex-1">
            <Search className="absolute start-4 top-1/2 h-4 w-4 -translate-y-1/2 text-text/40" />
            <input
              type="search"
              placeholder={t('news.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-full border border-border bg-white py-3 ps-11 pe-4 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <select
            value={year}
            onChange={(e) => setYear(e.target.value)}
            className="w-full rounded-full border border-border bg-white px-4 py-3 text-sm sm:w-auto"
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y === 'All' ? t('news.allYears') : y}
              </option>
            ))}
          </select>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full rounded-full border border-border bg-white px-4 py-3 text-sm sm:w-auto"
          >
            {categories.map((c) => (
              <option key={c} value={c}>
                {c === 'All' ? t('news.allCategories') : c}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <LoadingSpinner className="py-20" />
        ) : filtered.length === 0 ? (
          <p className="py-20 text-center text-text/60">{t('news.empty')}</p>
        ) : (
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="grid gap-8 md:grid-cols-2 lg:grid-cols-3"
          >
            {filtered.map((item) => (
              <motion.div key={item.id} variants={fadeInUp}>
                <Link to={`/news/${item.slug}`}>
                  <Card padding={false} className="group h-full overflow-hidden">
                    <div className="relative h-48 overflow-hidden">
                      <MediaImage
                        src={item.image || item.featured_image}
                        alt={localized(item, 'title')}
                        fallbackIndex={item.id}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-6">
                      <div className="mb-2 flex items-center gap-2 text-xs text-text/50">
                        <Calendar className="h-3.5 w-3.5" />
                        {item.published_at && formatDate(item.published_at)}
                      </div>
                      <h2 className="mb-2 text-lg font-bold text-primary group-hover:text-secondary">
                        {localized(item, 'title')}
                      </h2>
                      <p className="line-clamp-3 text-sm text-text/65">
                        {localized(item, 'excerpt')}
                      </p>
                    </div>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  )
}
